// ============================================================
// MAPA DE MERCADO + SIMULADOR DE RENDA
// Todas as 31 profissões da plataforma Portüs Talent Pro
// Dados enriquecidos — mercado digital brasileiro 2025-2026
// ============================================================

export const CATEGORY_COLORS = {
  "Conteúdo & Criação":      { bg: "#EAFAF6", text: "#00664C", dot: "#00DCA4" },
  "Marketing & Performance": { bg: "#D5F9D7", text: "#008572", dot: "#29A567" },
  "Vendas & Comercial":      { bg: "#FEF7B4", text: "#65541B", dot: "#B2911E" },
  "Dados & Operações":       { bg: "#FEEDB4", text: "#65541B", dot: "#B2911E" },
  "Produto & Estratégia":    { bg: "#B0FCEA", text: "#003326", dot: "#00664C" },
  "Comunidade & Atendimento":{ bg: "#FADBDF", text: "#892E38", dot: "#EF9A9E" },
  "Tecnologia":              { bg: "#F2F4F4", text: "#2E3233", dot: "#646C6F" },
};

export const MARKET_PROFESSIONS = [

  // ── CONTEÚDO & CRIAÇÃO ─────────────────────────────────────────────────────

  {
    id: "copywriter",
    name: "Copywriter",
    category: "Conteúdo & Criação",
    emoji: "✍️",
    tagline: "Escreve textos persuasivos que vendem produtos e serviços no digital",
    barrier: "alta",
    weekRoutine: [
      { day: "Seg", tasks: ["Briefing com cliente", "Pesquisa de mercado e concorrência", "Estudo de avatar e dores do público"] },
      { day: "Ter", tasks: ["Escrita de copy principal (página de vendas ou lançamento)", "Revisão com cliente"] },
      { day: "Qua", tasks: ["Ajustes e iterações de copy", "Headlines e copy para anúncios"] },
      { day: "Qui", tasks: ["Sequência de e-mails de venda", "Roteiro de VSL ou webinário"] },
      { day: "Sex", tasks: ["Análise de métricas de conversão", "Call estratégica com time de marketing"] },
    ],
    timeDistribution: [
      { label: "Criação & Escrita", pct: 50 },
      { label: "Pesquisa de Mercado", pct: 25 },
      { label: "Revisão & Ajustes", pct: 15 },
      { label: "Reuniões", pct: 10 },
    ],
    deliverables: ["Página de vendas completa", "Sequência de e-mails de nutrição e venda", "Roteiro de VSL", "Scripts de anúncios (Meta e Google)", "Bio e descrições para perfis", "Headlines para campanhas", "Copy de checkout e upsell"],
    tools: ["Google Docs", "Notion", "ChatGPT / Claude", "Meta Ads Library", "Hotmart", "Kiwify", "Copy.ai", "VTurb"],
    successProfile: ["Domínio de técnicas de persuasão e gatilhos mentais", "Sensibilidade emocional para se conectar com o público", "Tolera revisão e feedback constante sem perder a qualidade", "Curiosidade sobre psicologia humana e comportamento de compra", "Leitura frequente de referências de copy e vendas"],
    emotionalReality: {
      pressure: "Média-Alta",
      pains: ["Revisões intermináveis que diluem a força do texto", "Cliente que muda briefing no meio do projeto", "Bloqueio criativo sob pressão de prazos curtos", "Copy aprovado mas produto não converte por outros fatores"],
    },
    incomeData: {
      models: ["CLT", "PJ", "Freela"],
      scenarios: {
        junior: "R$2.5–4k",
        pleno: "R$4–7k",
        senior: "R$7–12k",
      },
    },
  },

  {
    id: "redator",
    name: "Redator",
    category: "Conteúdo & Criação",
    emoji: "📝",
    tagline: "Produz conteúdo editorial que informa, educa e posiciona marcas como autoridade",
    barrier: "baixa",
    weekRoutine: [
      { day: "Seg", tasks: ["Pauta editorial da semana", "Pesquisa de palavras-chave e tendências"] },
      { day: "Ter", tasks: ["Escrita de artigos e posts de blog", "Otimização de SEO on-page"] },
      { day: "Qua", tasks: ["Produção de roteiros para vídeo", "Revisão de conteúdos anteriores"] },
      { day: "Qui", tasks: ["Escrita para newsletters", "Alinhamento com estrategista de conteúdo"] },
      { day: "Sex", tasks: ["Entrega e agendamento de conteúdos", "Pesquisa de tendências para próxima semana"] },
    ],
    timeDistribution: [
      { label: "Escrita & Produção", pct: 55 },
      { label: "Pesquisa & SEO", pct: 25 },
      { label: "Revisão", pct: 15 },
      { label: "Reuniões", pct: 5 },
    ],
    deliverables: ["Artigos de blog otimizados para SEO", "Roteiros de vídeo e podcast", "Newsletters semanais", "Posts longos para LinkedIn e redes", "E-books e materiais ricos", "Descrições de produto", "Scripts de carrossel"],
    tools: ["Google Docs", "Notion", "SEMrush", "WordPress", "ChatGPT / Claude", "Surfer SEO", "Grammarly", "Ubersuggest"],
    successProfile: ["Escrita fluida e adaptável a diferentes tons de voz", "Curiosidade intelectual ampla para pesquisar qualquer tema", "Disciplina de produção diária mesmo sem inspiração", "Sensibilidade para adaptar linguagem ao público-alvo", "Domínio de SEO básico e estrutura de conteúdo web"],
    emotionalReality: {
      pressure: "Baixa",
      pains: ["Pauta fraca que não rende bom conteúdo", "Revisões que descaracterizam o texto original", "Criar sobre temas complexos com prazo curto", "Conteúdo bom que não recebe distribuição adequada"],
    },
    incomeData: {
      models: ["CLT", "PJ", "Freela"],
      scenarios: {
        junior: "R$1.8–3k",
        pleno: "R$3–5.5k",
        senior: "R$5.5–9k",
      },
    },
  },

  {
    id: "estrategista-conteudo",
    name: "Estrategista de Conteúdo",
    category: "Conteúdo & Criação",
    emoji: "🗺️",
    tagline: "Planeja, orquestra e mede toda a produção de conteúdo de uma marca",
    barrier: "alta",
    weekRoutine: [
      { day: "Seg", tasks: ["Análise de métricas de conteúdo da semana anterior", "Briefing de pauta para o time"] },
      { day: "Ter", tasks: ["Criação de calendário editorial mensal", "Alinhamento com redatores e designers"] },
      { day: "Qua", tasks: ["Revisão de conteúdos antes de publicação", "Estratégia de SEO e distribuição"] },
      { day: "Qui", tasks: ["Análise de performance por canal", "Pesquisa de tendências e benchmarking"] },
      { day: "Sex", tasks: ["Reunião estratégica com cliente ou liderança", "Planejamento de campanhas sazonais"] },
    ],
    timeDistribution: [
      { label: "Planejamento Estratégico", pct: 35 },
      { label: "Análise & Pesquisa", pct: 30 },
      { label: "Revisão de Conteúdo", pct: 20 },
      { label: "Reuniões & Alinhamentos", pct: 15 },
    ],
    deliverables: ["Calendário editorial completo", "Estratégia de conteúdo por canal", "Briefings detalhados para criadores", "Relatório mensal de performance", "Guia de tom de voz da marca", "Mapa de conteúdo por etapa do funil", "Plano de distribuição e reaproveitamento"],
    tools: ["Notion", "Google Analytics / GA4", "SEMrush", "Ahrefs", "ChatGPT / Claude", "Trello / Asana", "Metricool", "ContentSquare"],
    successProfile: ["Pensamento estratégico com visão de funil completo", "Combina análise de dados com sensibilidade criativa", "Organização de processos e fluxos editoriais", "Curiosidade sobre comportamento do público em cada canal", "Comunicação eficiente com times criativos e liderança"],
    emotionalReality: {
      pressure: "Média",
      pains: ["Conteúdo aprovado pelo cliente que nunca é publicado", "Time criativo que não segue briefing", "Estratégia boa que é mal executada na produção", "Ser cobrado por métricas de vaidade em vez de resultado real"],
    },
    incomeData: {
      models: ["CLT", "PJ", "Freela"],
      scenarios: {
        junior: "R$3–4.5k",
        pleno: "R$5–9k",
        senior: "R$9–15k",
      },
    },
  },

  {
    id: "social-media",
    name: "Social Media",
    category: "Conteúdo & Criação",
    emoji: "📱",
    tagline: "Gerencia a presença digital e o engajamento de marcas nas redes sociais",
    barrier: "baixa",
    weekRoutine: [
      { day: "Seg", tasks: ["Planejamento semanal de conteúdo", "Curadoria de referências e tendências"] },
      { day: "Ter", tasks: ["Criação de posts, carrosséis e artes", "Agendamento em ferramentas"] },
      { day: "Qua", tasks: ["Monitoramento de comentários e DMs", "Interação ativa com audiência"] },
      { day: "Qui", tasks: ["Produção de Stories, Reels e TikTok", "Análise de alcance e engajamento"] },
      { day: "Sex", tasks: ["Relatório semanal de métricas", "Reunião de alinhamento com cliente"] },
    ],
    timeDistribution: [
      { label: "Criação de Conteúdo", pct: 40 },
      { label: "Gestão de Comunidade", pct: 25 },
      { label: "Análise de Dados", pct: 20 },
      { label: "Planejamento & Reuniões", pct: 15 },
    ],
    deliverables: ["Calendário editorial semanal", "Posts para feed (estático e carrossel)", "Reels e vídeos curtos", "Stories diários", "Relatório mensal de métricas", "Estratégia de hashtags e SEO social", "Roteiros de conteúdo para TikTok"],
    tools: ["Meta Business Suite", "Canva", "CapCut", "Metricool", "Later / MLabs", "ChatGPT / Claude", "Notion", "TikTok Studio"],
    successProfile: ["Alta criatividade visual e senso estético", "Agilidade para surfar tendências em tempo real", "Facilidade de comunicação e tom de voz adaptável", "Organização de calendário e consistência de postagem", "Conforto com análise de métricas e dados de engajamento"],
    emotionalReality: {
      pressure: "Alta",
      pains: ["Algoritmo mudando regras sem aviso prévio", "Cliente que exige crescimento imediato sem investimento", "Criar conteúdo diário mesmo sem inspiração", "Trabalho invisível quando os números não crescem"],
    },
    incomeData: {
      models: ["CLT", "PJ", "Freela"],
      scenarios: {
        junior: "R$1.8–3k",
        pleno: "R$3.5–6k",
        senior: "R$6–10k",
      },
    },
  },

  {
    id: "designer",
    name: "Designer",
    category: "Conteúdo & Criação",
    emoji: "🎨",
    tagline: "Cria identidades visuais e materiais gráficos para marcas no digital",
    barrier: "alta",
    weekRoutine: [
      { day: "Seg", tasks: ["Briefing de novos projetos", "Pesquisa de referências e moodboard"] },
      { day: "Ter", tasks: ["Criação de layouts e primeiro draft", "Exploração de conceitos visuais"] },
      { day: "Qua", tasks: ["Apresentação para cliente", "Ajustes com base em feedback"] },
      { day: "Qui", tasks: ["Refinamento final de peças", "Exportação e organização de arquivos"] },
      { day: "Sex", tasks: ["Projetos menores (banners, posts)", "Atualização de portfólio"] },
    ],
    timeDistribution: [
      { label: "Criação Visual", pct: 55 },
      { label: "Pesquisa & Referências", pct: 20 },
      { label: "Revisões & Ajustes", pct: 15 },
      { label: "Reuniões & Apresentações", pct: 10 },
    ],
    deliverables: ["Identidade visual completa (logo, tipografia, paleta)", "Posts e peças para redes sociais", "Banners e criativos para anúncios", "Apresentações e pitch decks", "Material impresso digital (e-book, catálogo)", "Thumbnails e capas de vídeo", "Templates reutilizáveis para equipe"],
    tools: ["Figma", "Adobe Illustrator", "Photoshop", "Canva Pro", "Midjourney", "Adobe Express", "Notion"],
    successProfile: ["Sensibilidade estética apurada e repertório visual amplo", "Atenção obsessiva a detalhes de tipografia, cor e espaçamento", "Tolerância a revisões constantes mantendo qualidade", "Capacidade de traduzir briefing abstrato em visual concreto", "Atualizado em tendências de design e ferramentas de IA"],
    emotionalReality: {
      pressure: "Média",
      pains: ["Cliente que não sabe explicar o que quer", "Revisões subjetivas sem critério claro", "Prazo curto para projeto que exige profundidade criativa", "Design aprovado que é alterado por quem não é designer"],
    },
    incomeData: {
      models: ["CLT", "PJ", "Freela"],
      scenarios: {
        junior: "R$2–3.5k",
        pleno: "R$4–7k",
        senior: "R$7–13k",
      },
    },
  },

  {
    id: "web-designer",
    name: "Web Designer",
    category: "Conteúdo & Criação",
    emoji: "🖥️",
    tagline: "Projeta interfaces, landing pages e sites que convertem visitantes em clientes",
    barrier: "alta",
    weekRoutine: [
      { day: "Seg", tasks: ["Briefing e levantamento de requisitos", "Pesquisa de referências e benchmarking de sites"] },
      { day: "Ter", tasks: ["Wireframes e estrutura de páginas", "Revisão de arquitetura da informação"] },
      { day: "Qua", tasks: ["Design de alta fidelidade no Figma", "Feedback com cliente ou time"] },
      { day: "Qui", tasks: ["Ajustes, responsividade e interações", "Handoff para desenvolvimento"] },
      { day: "Sex", tasks: ["Testes de usabilidade básica", "Documentação de componentes e design tokens"] },
    ],
    timeDistribution: [
      { label: "Design de Interface", pct: 50 },
      { label: "Pesquisa & UX", pct: 25 },
      { label: "Revisões & Responsividade", pct: 15 },
      { label: "Reuniões & Handoff", pct: 10 },
    ],
    deliverables: ["Landing pages de alta conversão", "Protótipos navegáveis e interativos", "Design system básico com componentes", "Telas responsivas (mobile, tablet, desktop)", "Guia de estilos e design tokens", "Wireframes de baixa e alta fidelidade", "Sites completos em Webflow ou Framer"],
    tools: ["Figma", "Webflow", "Framer", "WordPress / Elementor", "Hotjar", "Canva", "Adobe XD", "Relume"],
    successProfile: ["Raciocínio visual e lógico combinados (estética + usabilidade)", "Atenção à experiência do usuário e fluxo de navegação", "Domínio de grid, tipografia e hierarquia visual", "Facilidade com ferramentas de prototipação e no-code", "Entende princípios de conversão e CRO básico"],
    emotionalReality: {
      pressure: "Média",
      pains: ["Implementação do dev que não respeita o design", "Cliente que quer colocar tudo na home", "Responsividade que quebra em dispositivos específicos", "Não ter acesso ao usuário real para testar hipóteses"],
    },
    incomeData: {
      models: ["CLT", "PJ", "Freela"],
      scenarios: {
        junior: "R$2.5–4k",
        pleno: "R$4.5–8k",
        senior: "R$8–14k",
      },
    },
  },

  {
    id: "videomaker",
    name: "Videomaker",
    category: "Conteúdo & Criação",
    emoji: "🎬",
    tagline: "Produz vídeos completos do roteiro à entrega final para marcas e criadores",
    barrier: "alta",
    weekRoutine: [
      { day: "Seg", tasks: ["Briefing com cliente e definição de conceito", "Roteiro e storyboard"] },
      { day: "Ter", tasks: ["Gravação externa ou em estúdio", "Direção e captura de imagens e áudio"] },
      { day: "Qua", tasks: ["Seleção de takes e organização de material", "Edição de corte e montagem"] },
      { day: "Qui", tasks: ["Colorização, mixagem de áudio e motion graphics", "Legendas e adequação para plataformas"] },
      { day: "Sex", tasks: ["Revisão com cliente e ajustes finais", "Exportação em múltiplos formatos"] },
    ],
    timeDistribution: [
      { label: "Edição & Pós-produção", pct: 40 },
      { label: "Gravação & Produção", pct: 30 },
      { label: "Planejamento & Roteiro", pct: 20 },
      { label: "Reuniões & Entregas", pct: 10 },
    ],
    deliverables: ["Vídeos longos para YouTube", "Conteúdo para redes sociais (Reels, Shorts, TikTok)", "Documentários de marca e brand films", "Vídeos institucionais e depoimentos", "Vídeos de produto e unboxing", "Conteúdo de bastidores e vlogs", "Vídeos de lançamento e eventos"],
    tools: ["Premiere Pro", "DaVinci Resolve", "After Effects", "CapCut", "Frame.io", "Riverside.fm", "Descript", "Runway"],
    successProfile: ["Olhar criativo para narrativa visual e storytelling", "Paciência com processos técnicos longos de pós-produção", "Habilidade de direção e comunicação com pessoas na frente da câmera", "Organização de arquivos e projetos pesados", "Sensibilidade sonora e visual combinadas"],
    emotionalReality: {
      pressure: "Média",
      pains: ["Cliente que muda conceito criativo após gravação", "Prazos impossíveis para produções complexas", "Arquivos corrompidos ou equipamento que falha", "Trabalho de dias condensado em vídeo de 30 segundos"],
    },
    incomeData: {
      models: ["PJ", "Freela"],
      scenarios: {
        junior: "R$2.5–4.5k",
        pleno: "R$5–8k",
        senior: "R$8–14k",
      },
    },
  },

  {
    id: "editor-videos",
    name: "Editor de Vídeos",
    category: "Conteúdo & Criação",
    emoji: "✂️",
    tagline: "Transforma gravações brutas em vídeos que prendem atenção e geram resultado",
    barrier: "baixa",
    weekRoutine: [
      { day: "Seg", tasks: ["Recebimento e organização de material bruto", "Análise de takes e seleção de melhores momentos"] },
      { day: "Ter", tasks: ["Corte e montagem principal", "Seleção de trilha sonora e efeitos"] },
      { day: "Qua", tasks: ["Legendas, motion graphics e elementos visuais", "Colorização básica"] },
      { day: "Qui", tasks: ["Revisão interna e controle de qualidade", "Envio para aprovação do cliente"] },
      { day: "Sex", tasks: ["Ajustes finais pós-feedback", "Exportação em múltiplos formatos e plataformas"] },
    ],
    timeDistribution: [
      { label: "Edição & Montagem", pct: 60 },
      { label: "Motion, Legendas & Efeitos", pct: 20 },
      { label: "Revisões & Ajustes", pct: 15 },
      { label: "Organização & Entrega", pct: 5 },
    ],
    deliverables: ["Vídeos editados para YouTube (longos e shorts)", "Reels e TikToks com cortes dinâmicos", "Vídeos de aula editados e segmentados", "Adaptações do mesmo vídeo para diferentes plataformas", "Vídeos com legendas animadas e motion graphics", "Compilações de cortes virais", "Trailers e teasers de lançamento"],
    tools: ["DaVinci Resolve", "Premiere Pro", "CapCut", "After Effects", "Descript", "Opus Clip", "Submagic", "Frame.io"],
    successProfile: ["Alta atenção a detalhes de timing, ritmo e transição", "Paciência com trabalho repetitivo sem perder qualidade", "Senso estético para corte que mantém atenção do espectador", "Organização meticulosa de projeto e arquivos pesados", "Autonomia e foco para trabalhar longos períodos sozinho"],
    emotionalReality: {
      pressure: "Baixa",
      pains: ["Material bruto de baixa qualidade de áudio ou vídeo", "Prazos curtos para edições longas e complexas", "Revisões que pedem mudanças profundas na montagem", "Trabalho técnico que é invisível para a audiência final"],
    },
    incomeData: {
      models: ["PJ", "Freela"],
      scenarios: {
        junior: "R$1.8–3k",
        pleno: "R$3.5–6k",
        senior: "R$6–10k",
      },
    },
  },

  // ── MARKETING & PERFORMANCE ────────────────────────────────────────────────

  {
    id: "analista-marketing",
    name: "Analista de Marketing",
    category: "Marketing & Performance",
    emoji: "📈",
    tagline: "Analisa dados de marketing e traduz métricas em decisões estratégicas",
    barrier: "alta",
    weekRoutine: [
      { day: "Seg", tasks: ["Análise de performance semanal de campanhas", "Atualização de dashboards e KPIs"] },
      { day: "Ter", tasks: ["Relatório detalhado de campanhas ativas", "Análise de funil de conversão por etapa"] },
      { day: "Qua", tasks: ["Pesquisa de concorrência e benchmarking", "Reunião de alinhamento com time de marketing"] },
      { day: "Qui", tasks: ["Apresentação de insights para liderança", "Sugestões de otimização baseadas em dados"] },
      { day: "Sex", tasks: ["Planejamento de campanhas futuras", "Atualização de relatório mensal consolidado"] },
    ],
    timeDistribution: [
      { label: "Análise de Dados", pct: 40 },
      { label: "Relatórios & Apresentações", pct: 25 },
      { label: "Planejamento", pct: 20 },
      { label: "Reuniões", pct: 15 },
    ],
    deliverables: ["Dashboard de métricas atualizado (semanal e mensal)", "Relatório de performance de campanhas", "Análise de funil de conversão", "Estudo de concorrência e benchmarking", "Recomendações estratégicas baseadas em dados", "Mapeamento de oportunidades de melhoria", "Report executivo para liderança"],
    tools: ["Google Analytics / GA4", "Looker Studio", "Power BI", "Meta Ads Manager", "Google Ads", "HubSpot", "SEMrush", "Google Sheets"],
    successProfile: ["Raciocínio analítico forte com capacidade de ver padrões", "Transforma dados complexos em histórias claras e acionáveis", "Curiosidade sobre comportamento do consumidor digital", "Comunicação visual de dados eficiente (gráficos, dashboards)", "Pensamento crítico para questionar métricas de vaidade"],
    emotionalReality: {
      pressure: "Média",
      pains: ["Dados inconsistentes ou ferramentas com tracking quebrado", "Explicar resultado ruim para gestor ou cliente", "Insight ignorado por quem toma decisões", "Campanha com bons números que não converte em vendas reais"],
    },
    incomeData: {
      models: ["CLT", "PJ"],
      scenarios: {
        junior: "R$2.5–4k",
        pleno: "R$5–8k",
        senior: "R$8–14k",
      },
    },
  },

  {
    id: "analista-email-marketing",
    name: "Analista de E-mail Marketing",
    category: "Marketing & Performance",
    emoji: "📧",
    tagline: "Cria, automatiza e otimiza campanhas de e-mail que nutrem leads e convertem vendas",
    barrier: "baixa",
    weekRoutine: [
      { day: "Seg", tasks: ["Análise de taxas de abertura, clique e conversão", "Planejamento de disparos da semana"] },
      { day: "Ter", tasks: ["Criação de templates e copywriting de e-mails", "Segmentação e limpeza de listas"] },
      { day: "Qua", tasks: ["Configuração de automações e fluxos de nutrição", "Testes A/B de assunto e CTA"] },
      { day: "Qui", tasks: ["Disparo de campanhas programadas", "Monitoramento de resultados em tempo real"] },
      { day: "Sex", tasks: ["Relatório de performance da semana", "Higienização de base e gestão de bounce"] },
    ],
    timeDistribution: [
      { label: "Criação de Campanhas", pct: 35 },
      { label: "Automações & Fluxos", pct: 25 },
      { label: "Análise & Relatórios", pct: 25 },
      { label: "Segmentação & Limpeza", pct: 15 },
    ],
    deliverables: ["Campanhas de e-mail segmentadas", "Fluxos de automação de nutrição e venda", "Relatório de performance (abertura, clique, conversão)", "Segmentações avançadas de base", "Testes A/B documentados com resultados", "Templates responsivos de e-mail", "Estratégia de reativação de leads frios"],
    tools: ["ActiveCampaign", "Klaviyo", "RD Station", "Mailchimp", "Google Sheets", "Canva", "Litmus", "Brevo"],
    successProfile: ["Perfil analítico com pitada criativa para copy", "Organização e atenção a detalhes de configuração", "Gosta de testar hipóteses e otimizar resultados", "Sensibilidade para escrita persuasiva em e-mail", "Entende comportamento do leitor e timing de envio"],
    emotionalReality: {
      pressure: "Baixa",
      pains: ["Base suja com alto bounce que prejudica entregabilidade", "Plataforma que muda regras de envio sem aviso", "E-mails bons que caem na caixa de spam", "Taxa de abertura caindo sem razão aparente"],
    },
    incomeData: {
      models: ["CLT", "PJ"],
      scenarios: {
        junior: "R$2–3.5k",
        pleno: "R$3.5–6k",
        senior: "R$6–10k",
      },
    },
  },

  {
    id: "gestor-trafego",
    name: "Gestor de Tráfego",
    category: "Marketing & Performance",
    emoji: "📊",
    tagline: "Gerencia investimento em anúncios pagos para gerar leads e vendas no digital",
    barrier: "alta",
    weekRoutine: [
      { day: "Seg", tasks: ["Análise de performance do final de semana", "Ajustes de lances, orçamentos e públicos"] },
      { day: "Ter", tasks: ["Criação de novas campanhas e estruturas", "Revisão de criativos com time de design"] },
      { day: "Qua", tasks: ["Otimização de públicos e segmentações", "Testes A/B de criativos e copy"] },
      { day: "Qui", tasks: ["Relatório parcial para clientes", "Análise detalhada de CPL, CPA e ROAS"] },
      { day: "Sex", tasks: ["Relatório semanal completo com insights", "Reunião de estratégia com time ou cliente"] },
    ],
    timeDistribution: [
      { label: "Análise & Otimização", pct: 40 },
      { label: "Criação de Campanhas", pct: 25 },
      { label: "Relatórios & Apresentações", pct: 20 },
      { label: "Reuniões & Estratégia", pct: 15 },
    ],
    deliverables: ["Estrutura completa de campanhas (Meta e Google)", "Relatórios de performance com análise de ROAS", "Plano de mídia com distribuição de verba", "Otimizações semanais documentadas", "Dashboard de métricas em tempo real", "Estratégia de escala de campanhas", "Criativos e copy para testes A/B"],
    tools: ["Meta Ads Manager", "Google Ads", "GA4", "Google Tag Manager", "Looker Studio", "Power BI", "VTurb", "TikTok Ads"],
    successProfile: ["Obsessão por números, otimização e ROI", "Tolerância à pressão constante por resultado", "Curioso sobre comportamento do consumidor online", "Disciplina rígida de rotina de análise diária", "Pensamento lógico e analítico combinado com criatividade"],
    emotionalReality: {
      pressure: "Alta",
      pains: ["Conta de anúncio bloqueada sem explicação", "Criativo que não converte apesar de boas métricas de engajamento", "Verba reduzida com meta mantida ou aumentada", "Ser cobrado por resultado quando o produto é fraco"],
    },
    incomeData: {
      models: ["CLT", "PJ", "Freela"],
      scenarios: {
        junior: "R$2.5–4.5k",
        pleno: "R$5–9k",
        senior: "R$9–16k",
      },
    },
  },

  {
    id: "gestor-marketing",
    name: "Gestor de Marketing",
    category: "Marketing & Performance",
    emoji: "📣",
    tagline: "Lidera equipes e define a estratégia de marketing digital de ponta a ponta",
    barrier: "alta",
    weekRoutine: [
      { day: "Seg", tasks: ["Revisão de métricas gerais de marketing", "Alinhamento semanal com o time"] },
      { day: "Ter", tasks: ["Reunião com stakeholders e liderança", "Definição e repriorização de demandas"] },
      { day: "Qua", tasks: ["Acompanhamento de campanhas em andamento", "Análise de funil completo (topo ao fundo)"] },
      { day: "Qui", tasks: ["Estratégia de novos canais e experimentos", "Revisão de orçamento de marketing"] },
      { day: "Sex", tasks: ["Relatório executivo para diretoria", "Planejamento mensal e trimestral"] },
    ],
    timeDistribution: [
      { label: "Estratégia & Planejamento", pct: 30 },
      { label: "Gestão de Time & 1:1s", pct: 30 },
      { label: "Análise de Resultados", pct: 25 },
      { label: "Reuniões com Liderança", pct: 15 },
    ],
    deliverables: ["Plano de marketing trimestral", "Relatório executivo de resultados", "Estratégia de canais e distribuição de verba", "OKRs e metas de marketing", "Análise de concorrência e posicionamento", "Plano de contratação e capacitação do time", "Budget review mensal"],
    tools: ["Google Analytics / GA4", "HubSpot", "Notion", "Power BI / Looker Studio", "Slack", "Asana / ClickUp", "SEMrush", "Meta Business Suite"],
    successProfile: ["Visão estratégica ampla com entendimento de negócio", "Liderança que inspira e desenvolve times", "Orientação total a resultados e dados", "Capacidade de influenciar decisões sem autoridade direta", "Comunicação executiva clara e objetiva com C-level"],
    emotionalReality: {
      pressure: "Alta",
      pains: ["Time desalinhado ou com skills insuficientes", "Orçamento insuficiente para atingir metas ambiciosas", "Cobranças de CEO sem contexto de marketing", "Resultados de longo prazo vs pressão por resultado imediato"],
    },
    incomeData: {
      models: ["CLT", "PJ"],
      scenarios: {
        junior: "R$4.5–7k",
        pleno: "R$8–13k",
        senior: "R$13–22k",
      },
    },
  },

  {
    id: "estrategista-digital",
    name: "Estrategista Digital",
    category: "Marketing & Performance",
    emoji: "🧠",
    tagline: "Define posicionamento, funil e estratégia digital de marcas e negócios",
    barrier: "alta",
    weekRoutine: [
      { day: "Seg", tasks: ["Análise de mercado, concorrência e oportunidades", "Revisão de posicionamento e diferenciação"] },
      { day: "Ter", tasks: ["Desenvolvimento de estratégia de canais", "Alinhamento com times de execução"] },
      { day: "Qua", tasks: ["Criação de plano tático mensal", "Reunião estratégica com cliente ou fundador"] },
      { day: "Qui", tasks: ["Análise de dados e ajuste de rota", "Documentação detalhada de estratégia"] },
      { day: "Sex", tasks: ["Apresentação executiva de resultados e próximos passos", "Planejamento trimestral"] },
    ],
    timeDistribution: [
      { label: "Estratégia & Planejamento", pct: 40 },
      { label: "Análise & Pesquisa", pct: 25 },
      { label: "Apresentações & Reports", pct: 20 },
      { label: "Alinhamentos & Mentoria", pct: 15 },
    ],
    deliverables: ["Plano estratégico digital completo", "Mapa de canais com papel de cada um no funil", "Posicionamento de marca documentado", "Funil de aquisição e retenção mapeado", "OKRs e metas por canal", "Análise SWOT digital", "Playbook de go-to-market"],
    tools: ["Notion", "Miro", "Google Analytics / GA4", "SEMrush", "Figma", "PowerPoint / Google Slides", "ChatGPT / Claude", "SimilarWeb"],
    successProfile: ["Visão sistêmica do ecossistema digital", "Capacidade de conectar negócio, produto, marketing e vendas", "Comunicação persuasiva com CEOs e fundadores", "Pensa em médio e longo prazo sem perder pragmatismo", "Aprende rápido sobre novos mercados e nichos"],
    emotionalReality: {
      pressure: "Média-Alta",
      pains: ["Estratégia aprovada que é mal executada pelo time", "Cliente que quer resultado imediato de estratégia de longo prazo", "Ser chamado só quando algo já deu errado", "Recomendação estratégica que não sai do papel"],
    },
    incomeData: {
      models: ["PJ", "Freela", "Consultoria"],
      scenarios: {
        junior: "R$3.5–6k",
        pleno: "R$7–12k",
        senior: "R$12–20k",
      },
    },
  },

  {
    id: "analista-infra-mkt",
    name: "Analista de Infraestrutura de Marketing Digital",
    category: "Marketing & Performance",
    emoji: "⚙️",
    tagline: "Configura e mantém toda a stack técnica que faz o marketing digital funcionar",
    barrier: "alta",
    weekRoutine: [
      { day: "Seg", tasks: ["Verificação de integrações e automações ativas", "Monitoramento de tracking e pixels"] },
      { day: "Ter", tasks: ["Configuração de eventos no Tag Manager", "Testes de pixels, tags e conversões"] },
      { day: "Qua", tasks: ["Implementação de novos fluxos de automação", "Documentação técnica atualizada"] },
      { day: "Qui", tasks: ["Auditoria de dados e CRM", "Suporte técnico ao time de tráfego e marketing"] },
      { day: "Sex", tasks: ["Relatório de saúde da infraestrutura", "Planejamento de melhorias e integrações"] },
    ],
    timeDistribution: [
      { label: "Configuração & Implementação", pct: 40 },
      { label: "Monitoramento & Auditoria", pct: 30 },
      { label: "Documentação & Processos", pct: 15 },
      { label: "Suporte & Reuniões", pct: 15 },
    ],
    deliverables: ["Stack de ferramentas configurada e documentada", "Tracking completo implementado (pixels, eventos, conversões)", "Fluxos de automação entre ferramentas", "Documentação técnica da infraestrutura", "Relatório de saúde de integrações", "Auditoria de dados e correções", "Setup de API e webhooks"],
    tools: ["Google Tag Manager", "Zapier", "Make (Integromat)", "N8N", "RD Station", "HubSpot", "Pixel Meta / CAPI", "Google Analytics / GA4"],
    successProfile: ["Perfil técnico com pensamento analítico", "Alta atenção a detalhes de configuração", "Gosta de processos, sistemas e automações", "Paciência para debugging e troubleshooting", "Ponte de comunicação entre times técnicos e de marketing"],
    emotionalReality: {
      pressure: "Baixa",
      pains: ["Integração que quebra sem motivo aparente", "Dados de analytics inconsistentes entre ferramentas", "Ferramenta que muda API ou regras sem aviso", "Trabalho técnico que só é notado quando algo quebra"],
    },
    incomeData: {
      models: ["CLT", "PJ"],
      scenarios: {
        junior: "R$3–5k",
        pleno: "R$5.5–9k",
        senior: "R$9–14k",
      },
    },
  },

  // ── VENDAS & COMERCIAL ─────────────────────────────────────────────────────

  {
    id: "gestor-comercial",
    name: "Gestor Comercial",
    category: "Vendas & Comercial",
    emoji: "💼",
    tagline: "Lidera equipes de vendas e estrutura processos comerciais para escalar receita",
    barrier: "alta",
    weekRoutine: [
      { day: "Seg", tasks: ["Pipeline review com o time", "Definição de metas e prioridades da semana", "1:1 com vendedores"] },
      { day: "Ter", tasks: ["Acompanhamento de negociações críticas", "Treinamento e roleplay com time"] },
      { day: "Qua", tasks: ["Reunião de alinhamento com marketing", "Análise de conversão por etapa do funil"] },
      { day: "Qui", tasks: ["Revisão de propostas e pricing", "Feedbacks individuais de performance"] },
      { day: "Sex", tasks: ["Relatório executivo de vendas para diretoria", "Planejamento da próxima semana"] },
    ],
    timeDistribution: [
      { label: "Gestão de Time & 1:1s", pct: 35 },
      { label: "Estratégia Comercial", pct: 25 },
      { label: "Análise de Pipeline", pct: 25 },
      { label: "Reuniões com Liderança", pct: 15 },
    ],
    deliverables: ["Relatório de vendas (semanal e mensal)", "Playbook comercial documentado", "Pipeline estruturado e atualizado", "Metas e OKRs de vendas", "Plano de comissionamento e incentivos", "Análise de forecast e projeções", "Plano de contratação e ramp-up"],
    tools: ["Pipedrive", "HubSpot", "Salesforce", "Google Sheets", "Zoom / Google Meet", "Notion", "Power BI", "Slack"],
    successProfile: ["Liderança inspiradora que desenvolve vendedores", "Orientação implacável a resultados e metas", "Alta inteligência emocional para lidar com pressão do time", "Experiência sólida em vendas consultivas e ciclo longo", "Capacidade de criar processos replicáveis e escaláveis"],
    emotionalReality: {
      pressure: "Muito Alta",
      pains: ["Time com performance inconsistente mês a mês", "Meta crescente trimestre a trimestre sem crescimento de time", "Turnover alto em posições de vendas", "Ser responsabilizado por resultado quando marketing não entrega leads"],
    },
    incomeData: {
      models: ["CLT", "PJ"],
      scenarios: {
        junior: "R$5–7k",
        pleno: "R$8–13k",
        senior: "R$13–22k",
      },
    },
  },

  {
    id: "closer",
    name: "Closer",
    category: "Vendas & Comercial",
    emoji: "🤝",
    tagline: "Fecha vendas de alto ticket em ligações e reuniões com leads qualificados",
    barrier: "baixa",
    weekRoutine: [
      { day: "Seg", tasks: ["Pipeline review e organização de agenda", "Follow-ups pendentes", "Preparação para calls do dia"] },
      { day: "Ter", tasks: ["Calls de fechamento (3-5 reuniões)", "Registro de objeções e status no CRM"] },
      { day: "Qua", tasks: ["Calls de fechamento", "Negociação ativa de propostas"] },
      { day: "Qui", tasks: ["Calls de fechamento", "Acompanhamento de propostas enviadas"] },
      { day: "Sex", tasks: ["Fechamento de funil semanal", "Análise de taxa de conversão e feedback com gestor"] },
    ],
    timeDistribution: [
      { label: "Calls de Fechamento", pct: 55 },
      { label: "Follow-ups & Negociação", pct: 25 },
      { label: "CRM & Organização", pct: 15 },
      { label: "Treinamento & Roleplay", pct: 5 },
    ],
    deliverables: ["Contratos fechados e receita gerada", "Relatório de conversão (agendamentos → vendas)", "Pipeline atualizado diariamente no CRM", "Análise de objeções recorrentes", "Propostas comerciais personalizadas", "Feedback de qualidade de leads para SDR/marketing"],
    tools: ["Pipedrive", "HubSpot", "Zoom / Google Meet", "WhatsApp Business", "Notion", "Loom"],
    successProfile: ["Alta energia e resiliência emocional para ouvir 'não'", "Tolerância elevada à rejeição sem perder confiança", "Conforto com pressão por meta e comissionamento", "Empatia genuína para entender a dor real do lead", "Escuta ativa avançada com capacidade de conduzir conversas"],
    emotionalReality: {
      pressure: "Muito Alta",
      pains: ["Leads frios ou desqualificados na agenda", "No-shows em reuniões agendadas", "Meta crescente todo mês sem aumento de leads", "Lead que some depois de parecer convicto"],
    },
    incomeData: {
      models: ["PJ", "Comissão"],
      scenarios: {
        junior: "R$2.5–4.5k",
        pleno: "R$5–9k",
        senior: "R$9–18k",
      },
    },
  },

  {
    id: "sdr",
    name: "SDR",
    category: "Vendas & Comercial",
    emoji: "📞",
    tagline: "Prospecta e qualifica leads para o time de vendas via inbound e outbound",
    barrier: "baixa",
    weekRoutine: [
      { day: "Seg", tasks: ["Lista de prospecção da semana", "Disparo de sequência de e-mails fria"] },
      { day: "Ter", tasks: ["Cold calls e abordagens ativas", "Outreach no LinkedIn"] },
      { day: "Qua", tasks: ["Follow-ups de conversas iniciadas", "Qualificação de leads quentes (BANT/SPIN)"] },
      { day: "Qui", tasks: ["Agendamento de reuniões para closers", "Atualização completa do CRM"] },
      { day: "Sex", tasks: ["Relatório de pipeline e métricas", "Análise de taxa de resposta e otimização de abordagem"] },
    ],
    timeDistribution: [
      { label: "Prospecção Ativa", pct: 50 },
      { label: "Follow-up & Qualificação", pct: 25 },
      { label: "CRM & Organização", pct: 15 },
      { label: "Reuniões & Treinamento", pct: 10 },
    ],
    deliverables: ["Reuniões qualificadas agendadas para closers", "Pipeline de leads qualificado", "Relatório de prospecção (volume e conversão)", "Sequências de e-mail otimizadas", "Cadências de prospecção documentadas", "Feedback de qualidade de leads para marketing"],
    tools: ["HubSpot", "Pipedrive", "Apollo.io", "LinkedIn Sales Navigator", "Ramper", "Notion", "Slack", "Lusha"],
    successProfile: ["Resiliência alta a rejeição diária constante", "Comunicação clara, objetiva e sem enrolação", "Disciplina de processo e cadência diária", "Energia e entusiasmo constante mesmo em dias ruins", "Curiosidade sobre negócios e mercados diferentes"],
    emotionalReality: {
      pressure: "Alta",
      pains: ["Ser ignorado dezenas de vezes por dia", "Lista de leads fria ou desatualizada", "Reunião agendada que dá no-show", "Meta de agendamentos impossível com volume de leads pequeno"],
    },
    incomeData: {
      models: ["CLT", "PJ"],
      scenarios: {
        junior: "R$1.8–3k",
        pleno: "R$3–5.5k",
        senior: "R$5.5–8k",
      },
    },
  },

  {
    id: "bdr",
    name: "BDR",
    category: "Vendas & Comercial",
    emoji: "🎯",
    tagline: "Abre novas contas e mercados via prospecção estratégica outbound",
    barrier: "baixa",
    weekRoutine: [
      { day: "Seg", tasks: ["Mapeamento de contas-alvo e ICP", "Pesquisa de decisores no LinkedIn e bases públicas"] },
      { day: "Ter", tasks: ["Outreach personalizado por e-mail e LinkedIn", "Cold calls estratégicos para decisores"] },
      { day: "Qua", tasks: ["Follow-ups e gestão de respostas", "Qualificação de interesse e fit"] },
      { day: "Qui", tasks: ["Agendamento de reuniões de discovery", "Atualização detalhada do CRM"] },
      { day: "Sex", tasks: ["Relatório de pipeline outbound", "Pesquisa de novos segmentos e verticais de mercado"] },
    ],
    timeDistribution: [
      { label: "Prospecção Outbound", pct: 50 },
      { label: "Pesquisa & Mapeamento", pct: 20 },
      { label: "Follow-up & Qualificação", pct: 20 },
      { label: "CRM & Relatórios", pct: 10 },
    ],
    deliverables: ["Novas contas abertas e qualificadas", "Pipeline outbound atualizado", "Relatório de mercado e oportunidades", "Sequências de abordagem personalizadas por vertical", "Mapeamento de decisores por conta", "Análise de conversão por canal de prospecção"],
    tools: ["LinkedIn Sales Navigator", "Apollo.io", "HubSpot", "Lusha", "Lemlist", "Notion", "Slack", "Hunter.io"],
    successProfile: ["Curiosidade genuína sobre negócios e mercados", "Persistência estratégica sem ser invasivo", "Capacidade de personalizar abordagem por conta", "Tolerância a ciclos longos sem resultado imediato", "Criatividade na forma de abordar e se conectar"],
    emotionalReality: {
      pressure: "Alta",
      pains: ["Empresa-alvo que nunca responde a nenhum canal", "Decisor que some no meio do processo de qualificação", "Ciclo de vendas longo com meta mensal", "Ser confundido com spam ou prospecção genérica"],
    },
    incomeData: {
      models: ["CLT", "PJ"],
      scenarios: {
        junior: "R$2–3.5k",
        pleno: "R$3.5–6k",
        senior: "R$6–10k",
      },
    },
  },

  {
    id: "vendedor-whatsapp",
    name: "Vendedor de WhatsApp",
    category: "Vendas & Comercial",
    emoji: "💬",
    tagline: "Converte leads e fecha vendas pelo WhatsApp com velocidade e técnica",
    barrier: "baixa",
    weekRoutine: [
      { day: "Seg", tasks: ["Triagem de leads recebidos no final de semana", "Follow-ups de conversas abertas"] },
      { day: "Ter", tasks: ["Atendimento ativo de novos leads", "Envio de propostas e links de pagamento"] },
      { day: "Qua", tasks: ["Recuperação de carrinho e leads frios", "Aplicação de scripts de objeção"] },
      { day: "Qui", tasks: ["Upsell para clientes recentes", "Atualização de CRM e status de conversas"] },
      { day: "Sex", tasks: ["Relatório de conversão da semana", "Revisão de scripts com gestor e time"] },
    ],
    timeDistribution: [
      { label: "Atendimento & Conversão", pct: 60 },
      { label: "Follow-up & Recuperação", pct: 20 },
      { label: "CRM & Organização", pct: 10 },
      { label: "Treinamento & Scripts", pct: 10 },
    ],
    deliverables: ["Volume de vendas fechadas por WhatsApp", "Taxa de conversão de leads recebidos", "Pipeline de WhatsApp atualizado", "Scripts otimizados por tipo de objeção", "Relatório de recuperação de carrinho", "Feedback de qualidade de leads para tráfego"],
    tools: ["WhatsApp Business", "CRM (Pipedrive, HubSpot)", "Hotmart / Kiwify", "Notion", "Google Sheets", "Typebot / ManyChat"],
    successProfile: ["Comunicação rápida e clara por texto", "Alta energia e agilidade de resposta em múltiplas conversas", "Resiliência a volume alto sem perder qualidade", "Empatia para entender a dor do lead via mensagem", "Disciplina de follow-up constante e organizado"],
    emotionalReality: {
      pressure: "Alta",
      pains: ["Lead que para de responder sem explicação", "Volume alto de conversas simultâneas sem conversão", "Produto com objeções difíceis de contornar por texto", "Meta alta em período com poucos leads entrando"],
    },
    incomeData: {
      models: ["CLT", "PJ", "Comissão"],
      scenarios: {
        junior: "R$1.5–3k",
        pleno: "R$3–5.5k",
        senior: "R$5.5–9k",
      },
    },
  },

  {
    id: "social-seller",
    name: "Social Seller",
    category: "Vendas & Comercial",
    emoji: "🤳",
    tagline: "Usa redes sociais para construir autoridade, atrair e converter clientes",
    barrier: "alta",
    weekRoutine: [
      { day: "Seg", tasks: ["Planejamento de conteúdo com foco em vendas", "Engajamento estratégico com ICP no LinkedIn/Instagram"] },
      { day: "Ter", tasks: ["Publicação de conteúdo de autoridade", "Prospecção via DM personalizada"] },
      { day: "Qua", tasks: ["Acompanhamento de leads gerados pelo conteúdo", "Respostas e conversas ativas"] },
      { day: "Qui", tasks: ["Calls de descoberta com leads aquecidos", "Follow-up de oportunidades em andamento"] },
      { day: "Sex", tasks: ["Análise de métricas de conteúdo e pipeline", "Revisão de estratégia de abordagem"] },
    ],
    timeDistribution: [
      { label: "Criação de Conteúdo", pct: 30 },
      { label: "Prospecção & DM", pct: 30 },
      { label: "Atendimento de Leads", pct: 25 },
      { label: "Análise & Estratégia", pct: 15 },
    ],
    deliverables: ["Leads qualificados gerados via conteúdo orgânico", "Pipeline de vendas via redes sociais", "Conteúdo de autoridade publicado com consistência", "Taxa de conversão de DM para call", "Relatório de vendas atribuídas a social selling", "Playbook de abordagem por rede social"],
    tools: ["LinkedIn", "Instagram", "Notion", "Pipedrive / HubSpot", "ChatGPT / Claude", "Canva", "Shield (LinkedIn analytics)", "Taplio"],
    successProfile: ["Conforto com exposição pública e construção de marca pessoal", "Combina criação de conteúdo com mentalidade comercial", "Autenticidade na comunicação que gera confiança", "Consistência de publicação por meses sem retorno imediato", "Paciência com ciclo longo de resultado orgânico"],
    emotionalReality: {
      pressure: "Média",
      pains: ["Conteúdo que gera engajamento mas não gera venda", "Meses de publicação consistente sem retorno financeiro visível", "Algoritmo que derruba alcance orgânico", "Ser visto como influencer e não como profissional de vendas"],
    },
    incomeData: {
      models: ["PJ", "Comissão", "Freela"],
      scenarios: {
        junior: "R$2–4k",
        pleno: "R$4–7k",
        senior: "R$7–12k",
      },
    },
  },

  // ── DADOS & OPERAÇÕES ──────────────────────────────────────────────────────

  {
    id: "sales-ops",
    name: "Sales Ops",
    category: "Dados & Operações",
    emoji: "🔧",
    tagline: "Estrutura processos, dados e ferramentas para escalar a operação de vendas",
    barrier: "alta",
    weekRoutine: [
      { day: "Seg", tasks: ["Análise de performance do funil de vendas", "Identificação de gargalos no processo comercial"] },
      { day: "Ter", tasks: ["Atualização de dashboards de vendas", "Configuração e manutenção do CRM"] },
      { day: "Qua", tasks: ["Criação ou ajuste de playbooks e processos", "Reunião de alinhamento com gestão comercial"] },
      { day: "Qui", tasks: ["Automações de processo comercial", "Análise de dados e projeções de forecast"] },
      { day: "Sex", tasks: ["Relatório executivo de operações", "Planejamento de melhorias e experimentos"] },
    ],
    timeDistribution: [
      { label: "Análise de Dados & Forecast", pct: 35 },
      { label: "Processos & Playbooks", pct: 25 },
      { label: "Automações & CRM", pct: 25 },
      { label: "Reuniões & Alinhamentos", pct: 15 },
    ],
    deliverables: ["Dashboard de vendas com métricas de funil", "Playbook comercial documentado e atualizado", "CRM configurado com automações", "Relatório de gargalos e oportunidades", "Forecast de receita", "Automações de processo (lead routing, notificações)", "Análise de produtividade do time comercial"],
    tools: ["Salesforce", "HubSpot", "Pipedrive", "Google Sheets", "Looker Studio", "Zapier / Make", "Notion", "Power BI"],
    successProfile: ["Perfil analítico e sistemático que adora processos", "Entende tanto de dados quanto de dinâmica comercial", "Alta atenção a detalhes de configuração e dados", "Gosta de documentar, padronizar e escalar operações", "Comunicação fluida com times de vendas, marketing e produto"],
    emotionalReality: {
      pressure: "Média",
      pains: ["Time de vendas que não segue o processo definido", "CRM desatualizado que distorce dados e forecast", "Mudança de processo frequente sem tempo de adaptação", "Trabalho estrutural que não é valorizado no dia a dia"],
    },
    incomeData: {
      models: ["CLT", "PJ"],
      scenarios: {
        junior: "R$3–5k",
        pleno: "R$6–10k",
        senior: "R$10–16k",
      },
    },
  },

  {
    id: "analista-crm",
    name: "Analista de CRM",
    category: "Dados & Operações",
    emoji: "🗃️",
    tagline: "Gerencia o relacionamento com leads e clientes via automações, segmentações e dados",
    barrier: "baixa",
    weekRoutine: [
      { day: "Seg", tasks: ["Análise de base de leads e saúde dos dados", "Segmentação de listas por comportamento"] },
      { day: "Ter", tasks: ["Criação e otimização de fluxos de automação", "Teste de e-mails e sequências"] },
      { day: "Qua", tasks: ["Análise de taxas de abertura, clique e conversão", "Otimização de sequências de nutrição"] },
      { day: "Qui", tasks: ["Integração de ferramentas e manutenção de dados", "Relatório de CRM para marketing e vendas"] },
      { day: "Sex", tasks: ["Análise de churn e reativação", "Reunião de alinhamento com marketing e vendas"] },
    ],
    timeDistribution: [
      { label: "Automações & Fluxos", pct: 35 },
      { label: "Análise de Dados", pct: 30 },
      { label: "Segmentação & Limpeza", pct: 20 },
      { label: "Reuniões & Alinhamentos", pct: 15 },
    ],
    deliverables: ["Fluxos de automação de nutrição e venda", "Relatório de e-mail marketing e CRM", "Segmentações avançadas por comportamento e perfil", "Dashboard de CRM atualizado", "Estratégia de nutrição por etapa do funil", "Higienização periódica de base", "Integrações entre ferramentas configuradas"],
    tools: ["HubSpot", "ActiveCampaign", "RD Station", "Mailchimp", "Google Sheets", "Zapier / Make", "Notion", "Brevo"],
    successProfile: ["Perfil analítico e metódico que gosta de padrões", "Organização e atenção a detalhes de configuração", "Gosta de processos, rotina e automação", "Curiosidade sobre comportamento do usuário e ciclo de vida", "Paciência para debugging de fluxos e integrações"],
    emotionalReality: {
      pressure: "Baixa",
      pains: ["Ferramenta de CRM que trava ou tem bugs recorrentes", "Base de dados suja que distorce métricas", "Processos legados que ninguém documentou", "Integração entre ferramentas que nunca funciona 100%"],
    },
    incomeData: {
      models: ["CLT", "PJ"],
      scenarios: {
        junior: "R$2.5–4k",
        pleno: "R$4.5–7.5k",
        senior: "R$7.5–12k",
      },
    },
  },

  {
    id: "gestor-produto",
    name: "Gestor de Produto Digital",
    category: "Produto & Estratégia",
    emoji: "🧩",
    tagline: "Lidera o desenvolvimento de produtos digitais conectando negócio, tecnologia e usuário",
    barrier: "alta",
    weekRoutine: [
      { day: "Seg", tasks: ["Sprint planning e priorização de backlog", "Alinhamento com time de engenharia"] },
      { day: "Ter", tasks: ["Reuniões com stakeholders e liderança", "Refinamento de features e user stories"] },
      { day: "Qua", tasks: ["Análise de dados de uso e comportamento", "Entrevistas com usuários e discovery"] },
      { day: "Qui", tasks: ["Review de entregas da sprint", "Documentação de produto (PRD, specs)"] },
      { day: "Sex", tasks: ["Retrospectiva com o time", "Planejamento e priorização da próxima sprint"] },
    ],
    timeDistribution: [
      { label: "Estratégia & Priorização", pct: 30 },
      { label: "Reuniões & Alinhamentos", pct: 30 },
      { label: "Análise de Dados & Discovery", pct: 25 },
      { label: "Documentação", pct: 15 },
    ],
    deliverables: ["Roadmap de produto trimestral", "PRD (Product Requirements Document)", "Métricas de produto (North Star, KPIs)", "Análise de comportamento do usuário", "Backlog priorizado e refinado", "Discovery documentado com hipóteses e resultados", "OKRs de produto alinhados com negócio"],
    tools: ["Notion", "Jira / Linear", "Figma", "Mixpanel / Amplitude", "Google Analytics", "Miro", "Hotjar", "Intercom"],
    successProfile: ["Visão estratégica e analítica combinadas com foco em impacto", "Conforto com ambiguidade e decisões com informação incompleta", "Liderança sem autoridade formal (influência por contexto)", "Comunicação clara com times técnicos e de negócio", "Obsessão pelo problema do usuário, não pela solução"],
    emotionalReality: {
      pressure: "Alta",
      pains: ["Conflito de prioridades entre stakeholders", "Escopo que cresce sem parar (scope creep)", "Times técnicos sem capacidade para a demanda", "Decisões revertidas por pressão política interna"],
    },
    incomeData: {
      models: ["CLT", "PJ"],
      scenarios: {
        junior: "R$5–8k",
        pleno: "R$9–16k",
        senior: "R$16–28k",
      },
    },
  },

  {
    id: "gestor-operacoes-digitais",
    name: "Gestor de Operações Digitais",
    category: "Dados & Operações",
    emoji: "⚡",
    tagline: "Garante que as operações digitais rodem de forma eficiente, documentada e escalável",
    barrier: "alta",
    weekRoutine: [
      { day: "Seg", tasks: ["Review de indicadores operacionais", "Identificação de gargalos e prioridades da semana"] },
      { day: "Ter", tasks: ["Otimização de fluxos de trabalho", "Reunião de alinhamento com times"] },
      { day: "Qua", tasks: ["Implementação de novas automações", "Documentação e atualização de SOPs"] },
      { day: "Qui", tasks: ["Análise de métricas operacionais", "Alinhamento com liderança sobre melhorias"] },
      { day: "Sex", tasks: ["Relatório de operações", "Planejamento de melhorias para a próxima semana"] },
    ],
    timeDistribution: [
      { label: "Gestão de Processos", pct: 35 },
      { label: "Análise Operacional", pct: 25 },
      { label: "Automações & Ferramentas", pct: 20 },
      { label: "Reuniões & Documentação", pct: 20 },
    ],
    deliverables: ["Mapa de processos da operação", "Relatório operacional semanal e mensal", "SOPs documentados e atualizados", "Automações implementadas entre ferramentas", "Dashboard de KPIs operacionais", "Plano de melhoria contínua", "Playbook de onboarding operacional"],
    tools: ["Notion", "Make (Integromat)", "Zapier", "N8N", "Asana / ClickUp", "Google Sheets", "Slack", "Loom"],
    successProfile: ["Visão sistêmica de processos de ponta a ponta", "Alta organização e disciplina de documentação", "Gosta de criar ordem a partir do caos operacional", "Comunicação eficiente com múltiplos times", "Orientação a eficiência, escalabilidade e resultado"],
    emotionalReality: {
      pressure: "Média-Alta",
      pains: ["Processo que quebra por falta de documentação", "Time que não segue os SOPs definidos", "Demanda operacional maior que a capacidade da equipe", "Trabalho de bastidor que não é reconhecido pela liderança"],
    },
    incomeData: {
      models: ["CLT", "PJ"],
      scenarios: {
        junior: "R$3.5–5.5k",
        pleno: "R$6–10k",
        senior: "R$10–17k",
      },
    },
  },

  {
    id: "gerente-projetos-digitais",
    name: "Gerente de Projetos Digitais",
    category: "Dados & Operações",
    emoji: "📋",
    tagline: "Coordena projetos digitais para que entregas aconteçam no prazo, no escopo e no budget",
    barrier: "alta",
    weekRoutine: [
      { day: "Seg", tasks: ["Status meeting do projeto com o time", "Atualização de cronograma e milestones"] },
      { day: "Ter", tasks: ["Acompanhamento de entregas por área", "Gestão de riscos e impedimentos"] },
      { day: "Qua", tasks: ["Reunião com cliente ou stakeholder", "Ajuste de escopo e repriorização"] },
      { day: "Qui", tasks: ["Atualização de documentação do projeto", "Reunião de alinhamento interno com liderança"] },
      { day: "Sex", tasks: ["Relatório de progresso semanal", "Planejamento detalhado da próxima semana"] },
    ],
    timeDistribution: [
      { label: "Coordenação & Reuniões", pct: 35 },
      { label: "Planejamento & Cronograma", pct: 30 },
      { label: "Documentação & Reports", pct: 20 },
      { label: "Gestão de Riscos & Escopo", pct: 15 },
    ],
    deliverables: ["Cronograma detalhado do projeto (Gantt ou board)", "Relatório de status semanal para stakeholders", "Plano de comunicação do projeto", "Escopo documentado com critérios de aceite", "Atas de reunião com decisões registradas", "Análise de riscos e plano de mitigação", "Retrospectiva e lições aprendidas"],
    tools: ["Asana / ClickUp", "Trello", "Notion", "Jira", "Google Workspace", "Slack", "Miro", "Monday.com"],
    successProfile: ["Organização extrema e proatividade na identificação de riscos", "Facilidade de comunicação com perfis diversos (técnico, negócio, cliente)", "Tolerância ao caos e à mudança constante de prioridades", "Visão do todo sem perder atenção aos detalhes críticos", "Gestão de expectativas realista com clientes e stakeholders"],
    emotionalReality: {
      pressure: "Alta",
      pains: ["Escopo que cresce sem aumento de orçamento ou prazo", "Time que perde deadline sem aviso prévio", "Cliente que muda requisito na reta final do projeto", "Ser o mensageiro de más notícias para todas as partes"],
    },
    incomeData: {
      models: ["CLT", "PJ"],
      scenarios: {
        junior: "R$3.5–5.5k",
        pleno: "R$6–10k",
        senior: "R$10–17k",
      },
    },
  },

  // ── COMUNIDADE & ATENDIMENTO ───────────────────────────────────────────────

  {
    id: "gestor-comunidade",
    name: "Gestor de Comunidade",
    category: "Comunidade & Atendimento",
    emoji: "🫂",
    tagline: "Cria e nutre comunidades digitais que geram pertencimento, engajamento e retenção",
    barrier: "baixa",
    weekRoutine: [
      { day: "Seg", tasks: ["Planejamento de ativações e conteúdo da semana", "Revisão de métricas de engajamento"] },
      { day: "Ter", tasks: ["Publicação de conteúdo na comunidade", "Facilitação de discussões e debates"] },
      { day: "Qua", tasks: ["Live ou evento online da comunidade", "Onboarding de novos membros"] },
      { day: "Qui", tasks: ["Moderação ativa e gestão de conflitos", "Contato com membros mais engajados (embaixadores)"] },
      { day: "Sex", tasks: ["Relatório de engajamento semanal", "Planejamento de evento ou ativação mensal"] },
    ],
    timeDistribution: [
      { label: "Facilitação & Engajamento", pct: 40 },
      { label: "Criação de Conteúdo", pct: 25 },
      { label: "Eventos & Ativações", pct: 20 },
      { label: "Análise & Relatórios", pct: 15 },
    ],
    deliverables: ["Calendário de ativações da comunidade", "Relatório de engajamento (mensal e semanal)", "Eventos online realizados (lives, AMAs, workshops)", "Programa de onboarding de novos membros", "Plano de crescimento e retenção da comunidade", "Programa de embaixadores ou membros destaque", "FAQ e recursos da comunidade"],
    tools: ["Discord", "Skool", "Circle.so", "Slack", "Hotmart Club", "Notion", "WhatsApp", "Loom"],
    successProfile: ["Empatia e escuta ativa genuínas com membros", "Facilidade de criar conexões entre pessoas diferentes", "Alta energia para interação constante e moderação", "Criatividade para ativações e dinâmicas que engajam", "Paciência para lidar com conflitos e membros difíceis"],
    emotionalReality: {
      pressure: "Média",
      pains: ["Membro tóxico que afeta o clima de toda a comunidade", "Comunidade que não engaja mesmo com conteúdo bom", "Crescimento lento comparado ao esforço investido", "Trabalho relacional que não tem métrica financeira clara"],
    },
    incomeData: {
      models: ["CLT", "PJ"],
      scenarios: {
        junior: "R$2.5–4k",
        pleno: "R$4–6.5k",
        senior: "R$6.5–10k",
      },
    },
  },

  {
    id: "gestor-atendimento-digital",
    name: "Gestor de Atendimento Digital",
    category: "Comunidade & Atendimento",
    emoji: "🎧",
    tagline: "Lidera a operação de suporte digital com foco em satisfação, eficiência e escala",
    barrier: "alta",
    weekRoutine: [
      { day: "Seg", tasks: ["Análise de tickets abertos e backlog", "Priorização de casos críticos e escalações"] },
      { day: "Ter", tasks: ["1:1 com time de atendimento", "Revisão e atualização de scripts e respostas padrão"] },
      { day: "Qua", tasks: ["Análise de NPS e pesquisas de satisfação", "Treinamento e capacitação do time"] },
      { day: "Qui", tasks: ["Alinhamento com produto sobre bugs recorrentes", "Melhoria de processos e fluxos de atendimento"] },
      { day: "Sex", tasks: ["Relatório de atendimento para liderança", "Planejamento de melhorias operacionais"] },
    ],
    timeDistribution: [
      { label: "Gestão de Time & Qualidade", pct: 35 },
      { label: "Análise de Satisfação & NPS", pct: 25 },
      { label: "Processos & Melhorias", pct: 25 },
      { label: "Reuniões & Alinhamentos", pct: 15 },
    ],
    deliverables: ["Relatório de satisfação do cliente (NPS, CSAT)", "Scripts e playbook de atendimento", "SLA de resposta definido e monitorado", "Treinamentos realizados e documentados", "Dashboard de tickets e métricas de atendimento", "Plano de melhoria contínua de suporte", "Análise de tickets recorrentes com recomendações para produto"],
    tools: ["Zendesk", "Intercom", "Freshdesk", "Notion", "Slack", "Google Sheets", "Typeform", "Loom"],
    successProfile: ["Empatia com cliente e com o time de atendimento", "Liderança calma e assertiva em situações de crise", "Orientação a processo, qualidade e melhoria contínua", "Alta tolerância a reclamações sem perder o foco", "Visão analítica para transformar feedback em ação"],
    emotionalReality: {
      pressure: "Alta",
      pains: ["Volume explosivo de atendimento em crises ou lançamentos", "Time com alta rotatividade e curva de aprendizado constante", "Produto com bugs recorrentes que geram tickets repetidos", "Resolver problemas de produto via atendimento paliativo"],
    },
    incomeData: {
      models: ["CLT", "PJ"],
      scenarios: {
        junior: "R$2.5–4k",
        pleno: "R$4.5–7.5k",
        senior: "R$7.5–12k",
      },
    },
  },

  {
    id: "analista-cs",
    name: "Analista de Customer Success",
    category: "Comunidade & Atendimento",
    emoji: "🌟",
    tagline: "Garante que clientes tenham sucesso real usando o produto, reduzindo churn e gerando expansão",
    barrier: "baixa",
    weekRoutine: [
      { day: "Seg", tasks: ["Review de health score da carteira de clientes", "Follow-ups prioritários de contas em risco"] },
      { day: "Ter", tasks: ["Calls de onboarding de novos clientes", "Resolução proativa de problemas identificados"] },
      { day: "Qua", tasks: ["QBRs (revisões trimestrais de resultados)", "Análise de churn risk e plano de ação"] },
      { day: "Qui", tasks: ["Identificação de oportunidades de upsell e expansão", "Documentação de cases de sucesso"] },
      { day: "Sex", tasks: ["Relatório da carteira para gestão", "Alinhamento com produto sobre feedbacks recorrentes"] },
    ],
    timeDistribution: [
      { label: "Atendimento & Calls", pct: 40 },
      { label: "Análise de Carteira & Dados", pct: 25 },
      { label: "Documentação & Processos", pct: 20 },
      { label: "Alinhamentos Internos", pct: 15 },
    ],
    deliverables: ["Health score atualizado da carteira", "Relatório de NPS e satisfação", "Plano de sucesso individualizado por cliente", "Análise de churn com causas e recomendações", "Cases de sucesso documentados", "Pipeline de upsell e expansão", "Feedback compilado para time de produto"],
    tools: ["HubSpot", "Intercom", "Zendesk", "Mixpanel", "Google Sheets", "Notion", "Gainsight", "ChurnZero"],
    successProfile: ["Empatia genuína com o cliente e seus objetivos", "Organização e follow-up constante sem esquecer ninguém", "Capacidade de identificar risco antes que vire cancelamento", "Comunicação proativa e clara sobre valor entregue", "Gosta de construir relacionamentos de longo prazo"],
    emotionalReality: {
      pressure: "Média",
      pains: ["Cliente que cancela sem dar chance de reverter", "Produto com bugs que você precisa defender diariamente", "Carteira grande demais com pouco suporte de time", "Ser cobrado por churn causado por problemas de produto"],
    },
    incomeData: {
      models: ["CLT", "PJ"],
      scenarios: {
        junior: "R$2.5–4k",
        pleno: "R$4.5–7.5k",
        senior: "R$7.5–12k",
      },
    },
  },

  {
    id: "analista-cx",
    name: "Analista de Customer Experience",
    category: "Comunidade & Atendimento",
    emoji: "⭐",
    tagline: "Mapeia e melhora a jornada do cliente em todos os pontos de contato com a marca",
    barrier: "alta",
    weekRoutine: [
      { day: "Seg", tasks: ["Análise de NPS, CSAT e pesquisas de satisfação", "Mapeamento de pontos de atrito na jornada"] },
      { day: "Ter", tasks: ["Entrevistas qualitativas com clientes", "Documentação e atualização do mapa de jornada"] },
      { day: "Qua", tasks: ["Reunião com produto e atendimento", "Proposta de melhorias baseada em dados"] },
      { day: "Qui", tasks: ["Acompanhamento de iniciativas de CX em andamento", "Análise de dados de comportamento do usuário"] },
      { day: "Sex", tasks: ["Relatório de CX para liderança", "Apresentação de insights e recomendações"] },
    ],
    timeDistribution: [
      { label: "Pesquisa & Análise", pct: 35 },
      { label: "Mapeamento de Jornada", pct: 25 },
      { label: "Reuniões & Apresentações", pct: 25 },
      { label: "Documentação & Relatórios", pct: 15 },
    ],
    deliverables: ["Mapa de jornada do cliente completo e atualizado", "Relatório de NPS e CSAT com análise qualitativa", "Plano de melhoria de experiência priorizado", "Pesquisas de satisfação configuradas e analisadas", "Dashboard de experiência do cliente", "Recomendações de melhoria para produto e atendimento", "Benchmark de CX com concorrentes"],
    tools: ["Typeform / SurveyMonkey", "Hotjar", "Mixpanel / Amplitude", "Notion", "Miro", "Google Analytics", "Intercom", "Dovetail"],
    successProfile: ["Empatia e curiosidade profunda sobre o cliente", "Perfil analítico com visão holística da experiência", "Facilidade de apresentar dados qualitativos de forma convincente", "Trabalha bem entre áreas (produto, marketing, atendimento)", "Orientação a melhoria contínua e impacto real"],
    emotionalReality: {
      pressure: "Média",
      pains: ["Insight de CX que não é priorizado pelo time de produto", "Dados qualitativos que são difíceis de quantificar para liderança", "Problemas sistêmicos que parecem simples de fora", "Recomendar mudanças e não ver implementação acontecer"],
    },
    incomeData: {
      models: ["CLT", "PJ"],
      scenarios: {
        junior: "R$2.5–4k",
        pleno: "R$4–7k",
        senior: "R$7–11k",
      },
    },
  },

  {
    id: "suporte-alunos",
    name: "Suporte a Alunos e Usuários",
    category: "Comunidade & Atendimento",
    emoji: "🙋",
    tagline: "Resolve dúvidas e problemas de alunos e usuários de plataformas digitais",
    barrier: "baixa",
    weekRoutine: [
      { day: "Seg", tasks: ["Triagem de tickets e dúvidas acumuladas", "Priorização por urgência e impacto"] },
      { day: "Ter", tasks: ["Atendimento ativo por e-mail, WhatsApp e chat", "Resolução de problemas técnicos e de acesso"] },
      { day: "Qua", tasks: ["Atualização de FAQ e documentação de dúvidas frequentes", "Alinhamento com produto sobre bugs"] },
      { day: "Qui", tasks: ["Atendimento proativo de alunos em risco de churn", "Registro e compilação de feedbacks"] },
      { day: "Sex", tasks: ["Relatório de tickets e satisfação", "Sugestões de melhoria de plataforma para produto"] },
    ],
    timeDistribution: [
      { label: "Atendimento Direto", pct: 65 },
      { label: "Documentação & FAQ", pct: 15 },
      { label: "Alinhamentos Internos", pct: 10 },
      { label: "Relatórios & Feedbacks", pct: 10 },
    ],
    deliverables: ["Tickets resolvidos dentro do SLA", "Base de FAQ atualizada e categorizada", "Relatório de satisfação dos atendimentos", "Feedbacks compilados e priorizados para produto", "Documentação de problemas recorrentes com soluções", "Relatório de volume e tipos de tickets"],
    tools: ["Zendesk", "Intercom", "Freshdesk", "WhatsApp Business", "Notion", "Google Sheets", "Loom", "Helpdesk interno"],
    successProfile: ["Paciência e empatia genuína em situações frustrantes", "Comunicação clara e simples adaptada ao nível do usuário", "Organização para gerenciar múltiplos atendimentos simultâneos", "Curiosidade técnica para resolver problemas de plataforma", "Resiliência emocional para lidar com reclamações diárias"],
    emotionalReality: {
      pressure: "Baixa",
      pains: ["Aluno frustrado por problema que não é culpa do suporte", "Problema técnico que não tem solução imediata disponível", "Volume explosivo de tickets em períodos de lançamento", "Resolver o mesmo problema repetidamente sem melhoria no produto"],
    },
    incomeData: {
      models: ["CLT", "PJ"],
      scenarios: {
        junior: "R$1.5–2.5k",
        pleno: "R$2.5–4k",
        senior: "R$4–7k",
      },
    },
  },

  // ── TECNOLOGIA ─────────────────────────────────────────────────────────────

  {
    id: "vibe-coder",
    name: "Desenvolvedor Vibe Coder",
    category: "Tecnologia",
    emoji: "💻",
    tagline: "Constrói soluções digitais usando IA como copiloto para resolver problemas de negócio",
    barrier: "alta",
    weekRoutine: [
      { day: "Seg", tasks: ["Planejamento do projeto e definição de requisitos", "Configuração de ambiente e escolha de stack"] },
      { day: "Ter", tasks: ["Desenvolvimento de features com auxílio de IA", "Integração com APIs e serviços externos"] },
      { day: "Qua", tasks: ["Testes, debugging e correção de bugs", "Revisão de código e refatoração"] },
      { day: "Qui", tasks: ["Deploy e publicação em produção", "Documentação básica e instruções de uso"] },
      { day: "Sex", tasks: ["Ajustes de feedback de usuários", "Exploração de novas ferramentas e técnicas de IA"] },
    ],
    timeDistribution: [
      { label: "Desenvolvimento & IA", pct: 55 },
      { label: "Planejamento & Arquitetura", pct: 20 },
      { label: "Testes & Debug", pct: 15 },
      { label: "Deploy & Documentação", pct: 10 },
    ],
    deliverables: ["Landing pages e sites funcionais", "Apps e ferramentas internas para negócios", "Automações inteligentes com IA", "Integrações entre sistemas e APIs", "MVPs de produto digital", "Dashboards e painéis de dados", "Chatbots e agentes de IA customizados"],
    tools: ["Cursor", "Claude Code", "ChatGPT", "Vercel", "Supabase", "GitHub", "V0", "Bolt / Lovable", "Figma", "Cloudflare"],
    successProfile: ["Perfil técnico com capacidade de aprender rápido usando IA", "Alta autonomia criativa e mentalidade de builder", "Conforto com aprendizado constante e ferramentas novas", "Mentalidade de produto (resolve problemas, não só código)", "Gosta de construir coisas concretas e ver resultado rápido"],
    emotionalReality: {
      pressure: "Média",
      pains: ["Bug impossível de encontrar mesmo com IA", "Escopo que cresce no meio do projeto sem ajuste de prazo", "Cliente que não entende o valor técnico do trabalho", "Isolamento de trabalhar sozinho em projetos complexos"],
    },
    incomeData: {
      models: ["PJ", "Freela", "Produto próprio"],
      scenarios: {
        junior: "R$3–5k",
        pleno: "R$6–11k",
        senior: "R$11–18k",
      },
    },
  },
];
